import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import background from "../assets/background.webp";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

const FORM_SUBMIT_URL = "/send-mail.php";

const Contact = () => {
  const location = useLocation();
  const [mounted, setMounted] = useState(false);
  const { t } = useTranslation();

  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [status, setStatus] = useState({
    type: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setMounted(false);

    const timer = setTimeout(() => {
      setMounted(true);
    }, 50);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.message.trim()
    ) {
      setStatus({
        type: "error",
        message: "Пожалуйста, заполните все поля.",
      });

      return;
    }

    setIsSubmitting(true);

    setStatus({
      type: "loading",
      message: "Отправка сообщения...",
    });

    try {
      const response = await fetch(FORM_SUBMIT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          type: "contact",
          name: form.name.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok || data.success !== true) {
        throw new Error(
          data.message || "Не удалось отправить сообщение."
        );
      }

      setStatus({
        type: "success",
        message: "Сообщение отправлено.",
      });

      setForm({
        name: "",
        email: "",
        message: "",
      });
    } catch (error) {
      console.error("CONTACT FORM ERROR:", error);

      setStatus({
        type: "error",
        message: "Ошибка отправки. Попробуйте позже.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="w-full min-h-screen bg-slate-800 text-white py-20 px-4 bg-cover bg-center"
      style={{ backgroundImage: `url(${background})` }}
    >
      <Helmet>
        <title>Contact Solar Holding - Get in Touch</title>

        <meta
          name="description"
          content="Contact Solar Holding for inquiries about solar energy solutions, projects, and partnerships. Reach out to our team today."
        />

        <meta
          name="keywords"
          content="contact Solar Holding, solar energy contact, renewable energy inquiry"
        />

        <meta
          property="og:title"
          content="Contact Solar Holding - Get in Touch"
        />

        <meta
          property="og:description"
          content="Get in touch with Solar Holding for solar energy solutions and partnerships."
        />

        <link
          rel="canonical"
          href="https://YOUR-DOMAIN.COM/contact"
        />
      </Helmet>

      {/* TITLE */}
      <div
        className={`text-center pt-24 max-sm:pt-6 mb-12 transition-all duration-700
        ${
          mounted
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10"
        }`}
      >
        <h1 className="text-[20px] md:text-[64px] uppercase tracking-[0.15em] font-['Cormorant_Garamond']">
          {t("contact_page_title")}
        </h1>

        <p className="text-yellow-400 tracking-[0.4em] uppercase mt-4 text-sm">
          {t("contact_page_subtitle")}
        </p>
      </div>

      {/* CONTENT */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10">

        {/* MAP */}
        <div
          className={`w-full h-[400px] max-sm:h-[300px] rounded-2xl overflow-hidden shadow-2xl transition-all duration-700 delay-100
          ${
            mounted
              ? "opacity-100 translate-x-0"
              : "opacity-0 -translate-x-10"
          }`}
        >
          <iframe
            title="map"
            src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d15813.668637288269!2d44.485081385710494!3d40.175647351211765!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m1!2zNDDCsDEwJzI4LjciTiA0NMKwMjknNTQuMCJF!5e0!3m2!1sen!2sam!4v1702384959543!5m2!1sen!2sam"
            className="w-full h-full"
            loading="lazy"
          />
        </div>

        {/* FORM */}
        <form
          onSubmit={handleSubmit}
          className={`bg-[#1e293b] p-6 max-sm:p-3 rounded-2xl shadow-2xl flex flex-col gap-4 transition-all duration-700 delay-200
          ${
            mounted
              ? "opacity-100 translate-x-0"
              : "opacity-0 translate-x-10"
          }`}
        >
          <input
            type="text"
            name="name"
            placeholder={t("contact_name_placeholder")}
            value={form.name}
            onChange={handleChange}
            autoComplete="name"
            maxLength={150}
            required
            className="p-3 rounded-lg bg-[#0f172a] border border-white/10 outline-none focus:border-yellow-400"
          />

          <input
            type="email"
            name="email"
            placeholder={t("contact_email_placeholder")}
            value={form.email}
            onChange={handleChange}
            autoComplete="email"
            maxLength={254}
            required
            className="p-3 rounded-lg bg-[#0f172a] border border-white/10 outline-none focus:border-yellow-400"
          />

          <textarea
            name="message"
            placeholder={t("contact_message_placeholder")}
            value={form.message}
            onChange={handleChange}
            rows={5}
            maxLength={10000}
            required
            className="p-3 rounded-lg bg-[#0f172a] border border-white/10 outline-none focus:border-yellow-400 resize-none"
          />

          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-yellow-400 text-black uppercase tracking-[0.2em] py-3 rounded-lg hover:bg-yellow-300 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting
              ? "Отправка..."
              : t("contact_send_button")}
          </button>

          {status.message && (
            <p
              role="status"
              aria-live="polite"
              className={`text-sm ${
                status.type === "success"
                  ? "text-green-400"
                  : status.type === "error"
                  ? "text-red-400"
                  : "text-white"
              }`}
            >
              {status.message}
            </p>
          )}
        </form>
      </div>
    </section>
  );
};

export default Contact;